// Kiosk palette + pure helpers (kept out of kioskUi.tsx so that file only
// exports components — keeps react-refresh / fast-refresh happy).

export type KioskMode = 'dark' | 'light'

export interface KioskPalette {
  bg: string
  panel: string
  panelHi: string
  line: string
  ink: string
  dim: string
  faint: string
  cyan: string
  green: string
  amber: string
  red: string
  violet: string
  /** Page backdrop gradient (the .kroot base layer). */
  rootGradient: string
  /** Top status bar gradient. */
  stripGradient: string
  /** Colour of the faint grid texture drawn over the backdrop. */
  gridLine: string
  /** Unfilled portion of bars / rings / gauges. Needs more weight than `line`
   *  in light mode, where a hairline grey on white reads as nothing. */
  gaugeTrack: string
  /** Alpha suffix for the START of a bar's fill gradient. Dark mode fades the
   *  fill toward the dark backdrop for depth; on white that same fade just
   *  makes the bar look washed out, so light mode keeps it solid. */
  fillAlpha: string
  /** Fill colour for gauges, rings and bars when no semantic colour applies. */
  gaugeFill: string
  /** Text drawn ON TOP of a solid accent fill (alert toasts). Dark mode's
   *  accents are bright, so near-black text wins there; light mode's are
   *  mid-dark jewel tones, so paper-white text wins. */
  onAccent: string
}

// Contrast for ink/dim/faint is measured against panelHi — the surface closest
// in luminance to the text, i.e. the worst case, in BOTH modes. WCAG AA (4.5:1)
// proved far too low a bar here: this is a wall display read from across a room
// at 10-11px, and greys that technically "passed" were reported as unreadable.
// The floors below are set by legibility in the room, and locked by a test:
// ink >= 14:1, dim >= 12:1, faint >= 8.5:1, accents >= 3:1.

export const DARK: KioskPalette = {
  bg: '#070a10',
  panel: '#0d121b',
  panelHi: '#121a28',
  line: '#1e2838',
  ink: '#e9effb',
  dim: '#d2d7e0',
  faint: '#aeb6c3',
  cyan: '#49b4ff',
  green: '#2fe6a0',
  amber: '#ffb648',
  red: '#ff5d73',
  violet: '#9f7bff',
  rootGradient:
    'radial-gradient(120% 80% at 15% -10%, rgba(73,180,255,.10), transparent 55%),' +
    'radial-gradient(120% 90% at 110% 120%, rgba(159,123,255,.10), transparent 55%),' +
    'linear-gradient(180deg,#080b12,#05070c)',
  stripGradient: 'linear-gradient(180deg,#0c1119,#080b12)',
  gridLine: 'rgba(255,255,255,.025)',
  gaugeTrack: '#1e2838',
  fillAlpha: 'bb',
  gaugeFill: '#2fe6a0',
  onAccent: '#070a10',
}

// Light mode is NOT the dark palette inverted, and it is not "white with the
// dark accents darkened" either — that reads as a wireframe: cold blue-grey
// backdrop, pure-white cards, steel-grey tracks, traffic-light accents.
//
// Daylight scene: a wall-mounted bar in a lit room, read from across it. The
// canvas is warm paper (OKLCH hue 85, chroma ≤ 0.011 — a linen tint, never
// yellow), with ink that is blue-black like print. Panels sit a hair above the
// backdrop; inset tiles sink back to it. Accents are jewel tones picked at a
// common OKLCH lightness (~0.55) so they read as one family: emerald, gold,
// crimson, indigo, cerulean. Every accent still clears 3:1 on panelHi and the
// saturation floor in the test, so they stay vivid on paper without shouting.
export const LIGHT: KioskPalette = {
  bg: '#f3f0e9',       // oklch(0.955 0.010 85) — warm linen backdrop
  panel: '#fdfcf9',    // oklch(0.99  0.004 85) — paper, not #fff
  panelHi: '#f0ece5',  // oklch(0.945 0.011 85) — inset tiles sink to the backdrop
  line: '#ddd9d0',     // oklch(0.885 0.013 85)
  ink: '#161b24',      // oklch(0.22 0.02 265) — blue-black print ink
  dim: '#242933',      // oklch(0.28 0.02 265)
  faint: '#3b404b',    // oklch(0.37 0.02 265)
  cyan: '#007cbb',     // oklch(0.55 0.15 235) — cerulean
  green: '#008149',    // oklch(0.52 0.15 160) — emerald, for text
  amber: '#be6800',    // oklch(0.60 0.17 70)  — gold, not brown, not orange
  red: '#d8151e',      // oklch(0.56 0.22 27)  — crimson
  violet: '#7c3aed',   // indigo-violet
  // One soft wash of warm light from the top-left, a whisper of ink-blue at
  // the bottom-right, over paper. No grid: on a light canvas it read as
  // graph paper under the tiles.
  rootGradient:
    'radial-gradient(110% 70% at 10% -10%, rgba(255,251,240,.9), transparent 55%),' +
    'radial-gradient(100% 80% at 105% 115%, rgba(22,27,36,.06), transparent 55%),' +
    'linear-gradient(180deg,#f4f1ea,#eeeae2)',
  stripGradient: 'linear-gradient(180deg,#fdfcf9,#f5f2eb)',
  gridLine: 'rgba(22,27,36,0)',
  gaugeTrack: '#ddd9d0',
  fillAlpha: 'ff',
  gaugeFill: '#009c5e', // oklch(0.60 0.16 160) — vivid emerald fill, 2.5:1 on its track
  onAccent: '#fdfcf9',
}

const STORAGE_KEY = 'zeus_kiosk_theme'

export function readStoredMode(): KioskMode {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'light' ? 'light' : 'dark'
  } catch {
    return 'dark' // private mode / storage disabled — dark is the safe default
  }
}

function persistMode(mode: KioskMode): void {
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    /* preference simply won't survive a reload */
  }
}

/**
 * The live palette.
 *
 * Deliberately a MUTATED module object rather than a React context or CSS
 * custom properties. There are ~160 `C.x` reads across nine kiosk components,
 * most of them SVG presentation attributes (`stroke={C.line}`); routing those
 * through a context would be a large mechanical refactor, and `var()` inside
 * SVG presentation attributes is a rendering-engine bet this wall display
 * cannot afford to lose. Mutating in place keeps every call site untouched.
 *
 * Callers must remount the tree after switching (see Kiosk.tsx), since React
 * has no way to know this object changed.
 */
export const C: KioskPalette = { ...DARK }

export function applyKioskTheme(mode: KioskMode, persist = true): void {
  Object.assign(C, mode === 'light' ? LIGHT : DARK)
  if (persist) persistMode(mode)
  try {
    document.documentElement.setAttribute('data-ktheme', mode)
  } catch {
    /* no DOM (tests) */
  }
}

export function tempColor(t: number | null | undefined): string {
  if (t == null) return C.dim
  if (t >= 85) return C.red
  if (t >= 72) return C.amber
  return C.green
}

export function pctColor(p: number | null | undefined): string {
  if (p == null) return C.dim
  if (p >= 90) return C.red
  if (p >= 70) return C.amber
  return C.cyan
}

export function fmt(n: number | null | undefined, digits = 0): string {
  return n == null
    ? '—'
    : n.toLocaleString('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: digits })
}
